/**
 * Innovation tab — Bill-only view (`rrm-exec-home-innovation.html`).
 *
 * Role-gate: only the Innovation Officer sees this tab. For v3.0 pilot that
 * maps to `wewg24@gmail.com` or any user whose role list includes 'Executive'
 * + their email equals the Bible-designated Innovation Officer.
 */

import { Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import type { InnovationKpi, RrmFilters } from './types';

const INNOV_KPIS: ReadonlyArray<InnovationKpi> = [
  {
    key: 'digital',
    icon: '📱',
    label: 'Digital Channel %',
    sub: 'RAO self-service / total opened',
    value: '42%',
    valueColor: 'var(--pcb-blue)',
    sparkStroke: 'var(--ent-loan)',
    sparkFill: 'rgba(41,104,173,0.15)',
    sparkPoints: '0,32 20,30 40,28 60,25 80,22 100,20 120,16 140,14 160,12 180,10 200,8',
    deltaDir: 'up',
    deltaText: '6pp MoM',
    targetText: 'Goal 50%',
  },
  {
    key: 'deflect',
    icon: '⇢',
    label: 'Deflection Conversion',
    sub: 'RAE booking → online app',
    value: '68%',
    valueColor: 'var(--ent-deposit)',
    sparkStroke: 'var(--ent-deposit)',
    sparkFill: 'rgba(3,131,135,0.15)',
    sparkPoints: '0,28 20,26 40,22 60,20 80,18 100,16 120,14 140,12 160,14 180,10 200,8',
    deltaDir: 'up',
    deltaText: '4pp MoM',
    targetText: 'Goal 75%',
  },
  {
    key: 'self',
    icon: '✓',
    label: 'Self-Service Completion',
    sub: 'Finished w/o FSR touch',
    value: '74%',
    valueColor: 'var(--pcb-green)',
    sparkStroke: 'var(--ent-relationship)',
    sparkFill: 'rgba(56,103,50,0.15)',
    sparkPoints: '0,22 20,24 40,22 60,18 80,20 100,15 120,14 140,12 160,11 180,12 200,10',
    deltaDir: 'up',
    deltaText: '3pp MoM',
    targetText: 'Goal 80%',
  },
  {
    key: 'auto',
    icon: '⏱',
    label: 'Automation Time Saved',
    sub: 'Pipelines × avg time saved',
    value: '194h',
    valueColor: 'var(--ent-ira)',
    sparkStroke: 'var(--ent-ira)',
    sparkFill: 'rgba(185,119,14,0.15)',
    sparkPoints: '0,30 20,28 40,25 60,22 80,18 100,14 120,16 140,10 160,8 180,6 200,5',
    deltaDir: 'up',
    deltaText: '28h MoM',
    targetText: '≈ $5,820/mo',
  },
  {
    key: 'ai',
    icon: '🤖',
    label: 'AI-Assist Accuracy',
    sub: 'ID scan + auto-decision agree',
    value: '91%',
    valueColor: 'var(--ent-application)',
    sparkStroke: 'var(--ent-application)',
    sparkFill: 'rgba(123,77,143,0.15)',
    sparkPoints: '0,20 20,18 40,15 60,14 80,12 100,13 120,12 140,10 160,9 180,8 200,7',
    deltaDir: 'up',
    deltaText: '2pp MoM',
    targetText: 'Goal 95%',
  },
];

const INSIGHTS: ReadonlyArray<{ n: number; body: JSX.Element; metric: string }> = [
  { n: 1, body: (<><strong>Digital channel crossed 40% for the first time</strong> — up 6pp from March. Driven by My Student Checking (86% digital) and Free Checking for the &lt; 30 age cohort.</>), metric: '+6pp MoM' },
  { n: 2, body: (<><strong>Automation saved 194 hours this month</strong> — approx. <strong>$5,820 in FSR opportunity cost</strong>. Highest saves: auto-CIP (72h), PDF render (51h), disclosure reconciler (28h).</>), metric: '194h' },
  { n: 3, body: (<><strong>AI-Assist ID scan accuracy held at 91%</strong> — still shy of the 95% goal. Failures concentrated on expired / partial-glare driver licenses. Recommend flag threshold adjust in RCM settings.</>), metric: '91% · 4pp gap' },
  { n: 4, body: (<><strong>Deflection funnel biggest drop at "Submit → Approved"</strong> — 83% step rate. Root cause: NSF on funding account (61%) + CIP review hold (24%). RAE can surface pre-flight NSF check.</>), metric: '17% drop' },
];

export interface InnovationTabProps {
  filters: RrmFilters;
}

/**
 * Innovation-Officer gate. For v3.0 pilot we allow any user whose primaryRole
 * is 'Executive' or 'SuperAdmin' plus a specific UPN matching the Innovation
 * Officer. Adjust in Wave 3 when `userRoles.roleName === 'InnovationOfficer'`
 * surfaces in `config.role.permissions`.
 */
function useIsInnovationOfficer(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.primaryRole === 'SuperAdmin') return true;
  if (user.primaryRole === 'Executive') return true;
  // Explicit Bill allowlist via UPN (matches Bible §4 Innovation Officer = Bill Wiggins).
  return user.upn.toLowerCase().includes('wiggins') || user.upn.toLowerCase().includes('wewg24');
}

export function InnovationTab({ filters: _filters }: InnovationTabProps): JSX.Element {
  const canView = useIsInnovationOfficer();

  if (!canView) {
    return (
      <div style={{ padding: 'var(--sp-11)', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--pcb-blue-dark)', marginBottom: 'var(--sp-5)' }}>Innovation Metrics</h2>
        <Pill variant="muted">🔒 Restricted view</Pill>
        <p style={{ color: 'var(--ink-600)', marginTop: 'var(--sp-6)' }}>
          This tab is reserved for the Innovation Officer. Contact Bill Wiggins for access.
        </p>
      </div>
    );
  }

  return (
    <>
      <section className="rrm-innov-hero">
        <div>
          <h2>💡 Digital transformation pulse · April 2026</h2>
          <p>
            Innovation Officer view: tracks how effectively customers are migrating from branch-bound
            flows into digital, self-service, and AI-assisted paths. Board-shareable as spotlight
            snapshots on request.
          </p>
        </div>
        <span className="rrm-innov-hero__badge">5 KPIs · 12-mo trend</span>
      </section>

      <div className="rrm-tile-strip">
        {INNOV_KPIS.map((k) => (
          <article key={k.key} className="rrm-innov-tile" style={{ borderTopColor: k.valueColor }}>
            <span className="rrm-innov-tile__icon" aria-hidden>{k.icon}</span>
            <div className="rrm-innov-tile__label">{k.label}</div>
            <div className="rrm-innov-tile__number" style={{ color: k.valueColor }}>{k.value}</div>
            <div className="rrm-innov-tile__sub">{k.sub}</div>
            <svg viewBox="0 0 200 40" preserveAspectRatio="none" width="100%" height={40} aria-hidden>
              <polyline points={k.sparkPoints} stroke={k.sparkStroke} strokeWidth={2.5} fill="none" />
              <polyline points={`${k.sparkPoints} 200,40 0,40`} fill={k.sparkFill} stroke="none" />
            </svg>
            <div className="rrm-innov-tile__footer">
              <span style={{ color: k.deltaDir === 'up' ? 'var(--pcb-green)' : 'var(--pcb-red)', fontWeight: 700, fontSize: 12 }}>
                {k.deltaDir === 'up' ? '↑' : k.deltaDir === 'down' ? '↓' : '→'} {k.deltaText}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>{k.targetText}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="rrm-chart-row">
        <article className="rrm-chart-card" aria-labelledby="rrm-funnel-h">
          <header className="rrm-chart-header">
            <div>
              <div className="rrm-chart-title" id="rrm-funnel-h">Apply-Online deflection funnel</div>
              <div className="rrm-chart-sub">RAE booking → application submitted, last 30 days</div>
            </div>
          </header>
          <div className="rrm-chart-body">
            {[
              { label: 'Booked appointment', pct: 100, count: 842, conv: '—', fill: 'var(--ent-loan)' },
              { label: 'Saw deflection prompt', pct: 89, count: 751, conv: '89% of prior', fill: 'var(--ent-loan)' },
              { label: 'Clicked "Apply Online"', pct: 76, count: 641, conv: '85% of prior', fill: 'var(--ent-deposit)' },
              { label: 'Started application', pct: 64, count: 539, conv: '84% of prior', fill: 'var(--ent-relationship)' },
              { label: 'Submitted application', pct: 53, count: 449, conv: '83% of prior', fill: 'var(--ent-ira)' },
              { label: 'Approved & funded', pct: 44, count: 370, conv: '82% of prior', fill: 'var(--ent-application)' },
            ].map((r) => (
              <div key={r.label} className="rrm-funnel-row">
                <span className="rrm-funnel-label">{r.label}</span>
                <div className="rrm-funnel-bar">
                  <div className="rrm-funnel-fill" style={{ width: `${r.pct}%`, background: r.fill }}>{r.pct}%</div>
                </div>
                <span className="rrm-funnel-value">
                  {r.count}
                  <span className="rrm-funnel-conv">{r.conv}</span>
                </span>
              </div>
            ))}
            <div style={{ marginTop: 'var(--sp-7)', padding: 'var(--sp-5)', background: 'var(--info-bg)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--info-text)' }}>
              <strong>End-to-end conversion: 44%</strong> · Avg time-to-fund: 12m 34s · Biggest drop-off: Submit → Approved (NSF or CIP)
            </div>
          </div>
        </article>

        <article className="rrm-chart-card" aria-labelledby="rrm-channel-mix-h">
          <header className="rrm-chart-header">
            <div>
              <div className="rrm-chart-title" id="rrm-channel-mix-h">Channel mix — 12 month trend</div>
              <div className="rrm-chart-sub">% of all opened accounts by originating channel</div>
            </div>
          </header>
          <div className="rrm-chart-body">
            <svg viewBox="0 0 620 280" preserveAspectRatio="none" width="100%" height={260} role="img" aria-label="Channel mix stacked area chart">
              <line x1={30} y1={30} x2={600} y2={30} stroke="var(--ink-200)" />
              <line x1={30} y1={90} x2={600} y2={90} stroke="var(--ink-200)" />
              <line x1={30} y1={150} x2={600} y2={150} stroke="var(--ink-200)" />
              <line x1={30} y1={210} x2={600} y2={210} stroke="var(--ink-200)" />
              <polygon points="50,210 95,205 140,200 185,195 230,190 275,188 320,180 365,170 410,160 455,155 500,148 545,140 590,130 590,210" fill="var(--ent-loan)" opacity={0.85} />
              <polygon points="50,180 95,175 140,168 185,162 230,158 275,150 320,142 365,138 410,130 455,125 500,118 545,110 590,100 590,130 545,140 500,148 455,155 410,160 365,170 320,180 275,188 230,190 185,195 140,200 95,205 50,210" fill="var(--ent-deposit)" opacity={0.85} />
              <polygon points="50,145 95,140 140,135 185,125 230,115 275,105 320,95 365,85 410,75 455,65 500,55 545,48 590,40 590,100 545,110 500,118 455,125 410,130 365,138 320,142 275,150 230,158 185,162 140,168 95,175 50,180" fill="var(--ent-application)" opacity={0.85} />
            </svg>
          </div>
        </article>
      </div>

      <article className="rrm-insight-card" aria-labelledby="rrm-insight-h">
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-6)' }}>
          <h3 id="rrm-insight-h" style={{ fontSize: 15, color: 'var(--ent-biz)', fontWeight: 700 }}>💡 Strategic insights (auto-flagged this month)</h3>
        </header>
        {INSIGHTS.map((ins) => (
          <div key={ins.n} className="rrm-insight-row">
            <span className="rrm-insight-bullet">{ins.n}</span>
            <div style={{ flex: 1 }}>{ins.body}</div>
            <span className="rrm-insight-metric">{ins.metric}</span>
          </div>
        ))}
      </article>
    </>
  );
}
